import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Award, CalendarDays, Clock, Users, Coins, Loader2, TrendingUp, Star, Inbox,
  CheckCircle, Shield, Upload, Eye, Trash2, Plus, FileText, Building2, X, Undo2, User
} from "lucide-react";

const statusStyle = { open: "bg-green-100 text-green-800", confirmed: "bg-blue-100 text-blue-800", completed: "bg-slate-100 text-slate-600", cancelled: "bg-red-100 text-red-800" };

const GOV_ID_TYPES = ["Passport", "Driver's License", "PhilSys National ID", "UMID", "PRC ID", "Postal ID", "Voter's ID"];
const ASSOCIATION_LEVELS = ["Provincial", "Regional", "National", "International"];

function ProofUpload({ label, value, onChange, disabled, testId }) {
  const ref = useRef(null);
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File must be under 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <div data-testid={testId}>
      <input ref={ref} type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={handleFile} className="hidden" />
      {value ? (
        <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded">
          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
          <span className="text-xs text-green-800 flex-1 truncate">Proof uploaded</span>
          <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => {
            const w = window.open("", "_blank");
            if (value.startsWith("data:application/pdf")) { w.document.write(`<iframe src="${value}" width="100%" height="100%" />`); }
            else { w.document.write(`<img src="${value}" style="max-width:100%" />`); }
          }}><Eye className="w-3 h-3" /></Button>
          {!disabled && <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => onChange(null)}><Trash2 className="w-3 h-3" /></Button>}
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" disabled={disabled}
          className="w-full text-xs border-dashed h-9" onClick={() => ref.current?.click()}>
          <Upload className="w-3 h-3 mr-1" />{label}
        </Button>
      )}
    </div>
  );
}

function LicenseRow({ item, idx, onChange, onRemove, disabled }) {
  const up = (k, v) => onChange(idx, { ...item, [k]: v });
  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3 bg-white" data-testid={`license-row-${idx}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-heading uppercase text-muted-foreground tracking-wider">License #{idx + 1}</span>
        {!disabled && <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => onRemove(idx)} data-testid={`remove-license-${idx}`}><X className="w-3 h-3" /></Button>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label className="text-[11px]">License Name *</Label><Input value={item.name || ""} onChange={e => up("name", e.target.value)} placeholder="e.g. PRC License" className="border-2 rounded-none text-sm" disabled={disabled} data-testid={`license-name-${idx}`} /></div>
        <div><Label className="text-[11px]">Issuing Body *</Label><Input value={item.issuing_body || ""} onChange={e => up("issuing_body", e.target.value)} placeholder="e.g. Professional Regulation Commission" className="border-2 rounded-none text-sm" disabled={disabled} data-testid={`license-issuing-${idx}`} /></div>
        <div><Label className="text-[11px]">License Number</Label><Input value={item.license_number || ""} onChange={e => up("license_number", e.target.value)} placeholder="e.g. 0012345" className="border-2 rounded-none text-sm" disabled={disabled} data-testid={`license-number-${idx}`} /></div>
        <div><Label className="text-[11px]">Expiry Date</Label><Input type="date" value={item.expiry_date || ""} onChange={e => up("expiry_date", e.target.value)} className="border-2 rounded-none text-sm" disabled={disabled} data-testid={`license-expiry-${idx}`} /></div>
      </div>
      <div><Label className="text-[11px]">Proof Document *</Label>
        <ProofUpload label="Upload license copy" value={item.proof_data} onChange={v => up("proof_data", v)} disabled={disabled} testId={`license-proof-${idx}`} />
      </div>
    </div>
  );
}

function AssociationRow({ item, idx, onChange, onRemove, disabled }) {
  const up = (k, v) => onChange(idx, { ...item, [k]: v });
  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3 bg-white" data-testid={`assoc-row-${idx}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-heading uppercase text-muted-foreground tracking-wider">Association #{idx + 1}</span>
        {!disabled && <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => onRemove(idx)} data-testid={`remove-assoc-${idx}`}><X className="w-3 h-3" /></Button>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label className="text-[11px]">Association Name *</Label><Input value={item.name || ""} onChange={e => up("name", e.target.value)} placeholder="e.g. Philippine Basketball Association" className="border-2 rounded-none text-sm" disabled={disabled} data-testid={`assoc-name-${idx}`} /></div>
        <div><Label className="text-[11px]">Membership Level *</Label>
          <Select value={item.membership_level || ""} onValueChange={v => up("membership_level", v)} disabled={disabled}>
            <SelectTrigger className="border-2 rounded-none text-sm" data-testid={`assoc-level-${idx}`}><SelectValue placeholder="Select level..." /></SelectTrigger>
            <SelectContent>{ASSOCIATION_LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-[11px]">Membership ID</Label><Input value={item.membership_id || ""} onChange={e => up("membership_id", e.target.value)} placeholder="e.g. PBA-2024-001" className="border-2 rounded-none text-sm" disabled={disabled} data-testid={`assoc-id-${idx}`} /></div>
      </div>
      <div><Label className="text-[11px]">Proof Document *</Label>
        <ProofUpload label="Upload membership proof" value={item.proof_data} onChange={v => up("proof_data", v)} disabled={disabled} testId={`assoc-proof-${idx}`} />
      </div>
    </div>
  );
}

export default function CoachDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingEvents, setPendingEvents] = useState(0);
  const navigate = useNavigate();

  // KYC state
  const [kyc, setKyc] = useState(null);
  const [kycLoading, setKycLoading] = useState(true);
  // Level 1 form
  const [govIdType, setGovIdType] = useState("");
  const [govIdFile, setGovIdFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [l1Submitting, setL1Submitting] = useState(false);
  const [l1Retracting, setL1Retracting] = useState(false);
  // Level 2 form
  const [licenses, setLicenses] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [l2Submitting, setL2Submitting] = useState(false);
  const [l2Retracting, setL2Retracting] = useState(false);

  const fetchKyc = async () => {
    try {
      const r = await api.get("/sp/kyc-status");
      setKyc(r.data);
      setGovIdType(r.data.gov_id_type || "");
      setLicenses(r.data.licenses?.length ? r.data.licenses : []);
      setAssociations(r.data.associations?.length ? r.data.associations : []);
    } catch { /* no kyc yet */ }
    setKycLoading(false);
  };

  useEffect(() => {
    api.get("/coach/dashboard").then(r => { setData(r.data); setPendingEvents(r.data.pending_event_requests || 0); setLoading(false); }).catch(() => setLoading(false));
    fetchKyc();
  }, []);

  // KYC computed
  const kycLevel = kyc?.kyc_level || 1;
  const l1Status = kyc?.identity_review_status;
  const l1Editable = !l1Status || l1Status === "retracted" || l1Status === "rejected" || l1Status === "incomplete";
  const l1Pending = l1Status === "pending" || l1Status === "under_review";
  const l2Status = kyc?.credentials_review_status;
  const l2Editable = !l2Status || l2Status === "retracted" || l2Status === "rejected" || l2Status === "incomplete";
  const l2Pending = l2Status === "pending" || l2Status === "under_review";

  // Level 1 submit
  const handleL1Submit = async () => {
    if (!govIdType) { toast.error("Select a government ID type"); return; }
    if (!govIdFile && !kyc?.gov_id_url) { toast.error("Upload your government ID"); return; }
    if (!selfieFile && !kyc?.selfie_url) { toast.error("Upload a selfie holding your ID"); return; }
    setL1Submitting(true);
    try {
      let govIdUrl = kyc?.gov_id_url || "";
      let selfieUrl = kyc?.selfie_url || "";
      if (govIdFile) {
        const fd = new FormData(); fd.append("file", govIdFile); fd.append("doc_type", "government_id"); fd.append("category", "sp_kyc_identity");
        const r = await api.post("/uploads/kyc-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
        govIdUrl = r.data.url;
      }
      if (selfieFile) {
        const fd = new FormData(); fd.append("file", selfieFile); fd.append("doc_type", "selfie"); fd.append("category", "sp_kyc_identity");
        const r = await api.post("/uploads/kyc-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
        selfieUrl = r.data.url;
      }
      await api.post("/sp/kyc/identity", { gov_id_type: govIdType, gov_id_url: govIdUrl, selfie_url: selfieUrl });
      toast.success("Identity documents submitted for review!");
      await fetchKyc();
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
    setL1Submitting(false);
  };

  const handleL1Retract = async () => {
    setL1Retracting(true);
    try {
      await api.post("/sp/kyc/identity/retract");
      toast.success("Identity submission retracted. You may now edit and resubmit.");
      await fetchKyc();
    } catch (e) { toast.error(e.response?.data?.detail || "Cannot retract yet"); }
    setL1Retracting(false);
  };

  // Level 2 submit
  const handleL2Submit = async () => {
    const validLicenses = licenses.filter(l => l.name && l.issuing_body);
    const validAssocs = associations.filter(a => a.name && a.membership_level);
    if (validLicenses.length === 0 && validAssocs.length === 0) {
      toast.error("Add at least one license or association"); return;
    }
    for (const l of validLicenses) {
      if (!l.proof_data) { toast.error(`Upload proof for license: ${l.name}`); return; }
    }
    for (const a of validAssocs) {
      if (!a.proof_data) { toast.error(`Upload proof for association: ${a.name}`); return; }
    }
    setL2Submitting(true);
    try {
      await api.post("/sp/kyc/credentials", { licenses: validLicenses, associations: validAssocs });
      toast.success("Professional information submitted for review!");
      await fetchKyc();
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
    setL2Submitting(false);
  };

  const handleL2Retract = async () => {
    setL2Retracting(true);
    try {
      await api.post("/sp/kyc/credentials/retract");
      toast.success("Credentials retracted. You may now edit and resubmit.");
      await fetchKyc();
    } catch (e) { toast.error(e.response?.data?.detail || "Cannot retract yet"); }
    setL2Retracting(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;
  if (!data) return <div className="text-center py-20 font-heading text-xl">Unable to load dashboard</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="coach-dashboard">
      <div className="flex items-center gap-3 mb-2">
        <Award className="w-7 h-7 text-court-orange" />
        <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">Coach Dashboard</h1>
      </div>
      <p className="text-muted-foreground mb-8">Welcome back, {data.coach_name}</p>

      {/* KYC Progress */}
      {!kycLoading && (
        <section className="bg-white border-2 border-slate-100 p-6 mb-8" data-testid="kyc-progress-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold uppercase tracking-tight flex items-center gap-2">
              <Shield className="w-5 h-5 text-court-orange" />Verification Progress
            </h2>
            <Badge className={kycLevel >= 3 ? "bg-green-100 text-green-800" : kycLevel >= 2 ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"} data-testid="kyc-level-badge">
              Level {kycLevel}
            </Badge>
          </div>

          {/* Level Steps */}
          <div className="grid grid-cols-4 gap-2 mb-6">
            {[
              { lvl: 1, label: "Registered" },
              { lvl: 2, label: "ID Verified" },
              { lvl: 3, label: "Professional" },
              { lvl: 4, label: "Payout Ready" },
            ].map(s => (
              <div key={s.lvl} className={`text-center p-3 border ${kycLevel >= s.lvl ? "bg-green-50 border-green-200" : "border-dashed border-slate-200"}`} data-testid={`kyc-step-${s.lvl}`}>
                <CheckCircle className={`w-5 h-5 mx-auto mb-1 ${kycLevel >= s.lvl ? "text-green-500" : "text-slate-200"}`} />
                <p className="text-[10px] font-heading uppercase font-medium">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Level 1: Identity Verification */}
          {kycLevel < 2 && (
            <div className="bg-blue-50 border border-blue-200 p-5 mb-4 space-y-4" data-testid="kyc-level1-form">
              <div>
                <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-blue-800 mb-1">Level 1 — Identity Verification</h3>
                <p className="text-xs text-blue-700">Submit a valid government ID and selfie to verify your identity.</p>
              </div>

              {l1Editable ? (
                <>
                  <div>
                    <Label className="text-xs font-heading uppercase">Government ID Type *</Label>
                    <Select value={govIdType} onValueChange={setGovIdType}>
                      <SelectTrigger className="border-2 rounded-none bg-white" data-testid="l1-gov-id-type"><SelectValue placeholder="Select ID type..." /></SelectTrigger>
                      <SelectContent>{GOV_ID_TYPES.map(id => <SelectItem key={id} value={id}>{id}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-heading uppercase">Government ID Photo *</Label>
                      {kyc?.gov_id_url && !govIdFile ? (
                        <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded mt-1">
                          <CheckCircle className="w-4 h-4 text-green-500" /><span className="text-xs flex-1">ID uploaded</span>
                          <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => window.open(kyc.gov_id_url, '_blank')}><Eye className="w-3 h-3 mr-1" />View</Button>
                        </div>
                      ) : (
                        <div className="mt-1">
                          <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={e => setGovIdFile(e.target.files?.[0])} className="hidden" id="l1-gov-upload" />
                          <label htmlFor="l1-gov-upload" className="border-2 border-dashed p-3 text-center block cursor-pointer hover:border-blue-400 transition-colors rounded">
                            <Upload className="w-6 h-6 text-blue-400 mx-auto mb-1" />
                            <p className="text-xs font-medium">{govIdFile ? govIdFile.name : "Click to upload"}</p>
                          </label>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs font-heading uppercase">Selfie with ID *</Label>
                      {kyc?.selfie_url && !selfieFile ? (
                        <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded mt-1">
                          <CheckCircle className="w-4 h-4 text-green-500" /><span className="text-xs flex-1">Selfie uploaded</span>
                          <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => window.open(kyc.selfie_url, '_blank')}><Eye className="w-3 h-3 mr-1" />View</Button>
                        </div>
                      ) : (
                        <div className="mt-1">
                          <input type="file" accept=".jpg,.jpeg,.png" onChange={e => setSelfieFile(e.target.files?.[0])} className="hidden" id="l1-selfie-upload" />
                          <label htmlFor="l1-selfie-upload" className="border-2 border-dashed p-3 text-center block cursor-pointer hover:border-blue-400 transition-colors rounded">
                            <User className="w-6 h-6 text-blue-400 mx-auto mb-1" />
                            <p className="text-xs font-medium">{selfieFile ? selfieFile.name : "Click to upload"}</p>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button className="w-full rounded-full bg-blue-600 hover:bg-blue-700 text-white font-heading uppercase text-xs"
                    disabled={!govIdType || l1Submitting || (!govIdFile && !kyc?.gov_id_url) || (!selfieFile && !kyc?.selfie_url)}
                    onClick={handleL1Submit} data-testid="l1-submit-btn">
                    {l1Submitting ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Submitting...</> : <><CheckCircle className="w-3 h-3 mr-1" />Submit Identity Documents</>}
                  </Button>
                </>
              ) : l1Pending ? (
                <div className="space-y-3">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                    <p className="text-sm font-medium text-amber-800">Under Review — Your identity documents are being reviewed by the admin team.</p>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-full text-xs border-2" disabled={l1Retracting} onClick={handleL1Retract} data-testid="l1-retract-btn">
                    <Undo2 className="w-3 h-3 mr-1" />{l1Retracting ? "Retracting..." : "Retract & Edit (30min cooldown)"}
                  </Button>
                </div>
              ) : null}

              {l1Status === "rejected" && (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm font-medium text-red-800">Identity Rejected</p>
                  {kyc?.identity_review_notes && <p className="text-xs text-red-700 mt-1">{kyc.identity_review_notes}</p>}
                </div>
              )}
            </div>
          )}

          {kycLevel >= 2 && kyc?.gov_id_type && (
            <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 mb-4" data-testid="l1-verified-badge">
              <CheckCircle className="w-4 h-4 text-green-500" /><span className="text-sm font-medium text-green-800">Level 1 Verified — {kyc.gov_id_type}</span>
            </div>
          )}

          {/* Level 2: Professional Information */}
          {kycLevel >= 2 && kycLevel < 3 && (
            <div className="bg-purple-50 border border-purple-200 p-5 space-y-5" data-testid="kyc-level2-form">
              <div>
                <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-purple-800 mb-1">Level 2 — Professional Information</h3>
                <p className="text-xs text-purple-700">Submit your professional licenses and association memberships to unlock bookings and sessions.</p>
              </div>

              {l2Editable ? (
                <>
                  {/* Licenses Section */}
                  <div className="space-y-3" data-testid="licenses-section">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-heading uppercase flex items-center gap-1"><FileText className="w-3 h-3" />Professional Licenses</Label>
                      <Button type="button" variant="outline" size="sm" className="text-xs h-7 rounded-full"
                        onClick={() => setLicenses(prev => [...prev, { name: "", issuing_body: "", license_number: "", expiry_date: "", proof_data: null }])} data-testid="add-license-btn">
                        <Plus className="w-3 h-3 mr-1" />Add License
                      </Button>
                    </div>
                    {licenses.length === 0 && <p className="text-xs text-muted-foreground italic">No licenses added yet. Click "Add License" above.</p>}
                    {licenses.map((lic, i) => (
                      <LicenseRow key={i} item={lic} idx={i}
                        onChange={(idx, val) => setLicenses(prev => prev.map((l, j) => j === idx ? val : l))}
                        onRemove={(idx) => setLicenses(prev => prev.filter((_, j) => j !== idx))} disabled={false} />
                    ))}
                  </div>

                  {/* Associations Section */}
                  <div className="space-y-3 pt-3 border-t border-purple-200" data-testid="associations-section">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-heading uppercase flex items-center gap-1"><Building2 className="w-3 h-3" />Association Memberships</Label>
                      <Button type="button" variant="outline" size="sm" className="text-xs h-7 rounded-full"
                        onClick={() => setAssociations(prev => [...prev, { name: "", membership_level: "", membership_id: "", proof_data: null }])} data-testid="add-assoc-btn">
                        <Plus className="w-3 h-3 mr-1" />Add Association
                      </Button>
                    </div>
                    {associations.length === 0 && <p className="text-xs text-muted-foreground italic">No associations added yet. Click "Add Association" above.</p>}
                    {associations.map((assoc, i) => (
                      <AssociationRow key={i} item={assoc} idx={i}
                        onChange={(idx, val) => setAssociations(prev => prev.map((a, j) => j === idx ? val : a))}
                        onRemove={(idx) => setAssociations(prev => prev.filter((_, j) => j !== idx))} disabled={false} />
                    ))}
                  </div>

                  <Button className="w-full rounded-full bg-purple-600 hover:bg-purple-700 text-white font-heading uppercase text-xs"
                    disabled={l2Submitting || (licenses.filter(l => l.name).length === 0 && associations.filter(a => a.name).length === 0)}
                    onClick={handleL2Submit} data-testid="l2-submit-btn">
                    {l2Submitting ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Submitting...</> : <><CheckCircle className="w-3 h-3 mr-1" />Submit Professional Information</>}
                  </Button>
                </>
              ) : l2Pending ? (
                <div className="space-y-3">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                    <p className="text-sm font-medium text-amber-800">Under Review — Your professional credentials are being reviewed.</p>
                  </div>
                  {/* Show submitted licenses/associations read-only */}
                  {kyc?.licenses?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-heading uppercase text-muted-foreground">Submitted Licenses</p>
                      {kyc.licenses.map((l, i) => <LicenseRow key={i} item={l} idx={i} onChange={() => {}} onRemove={() => {}} disabled />)}
                    </div>
                  )}
                  {kyc?.associations?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-heading uppercase text-muted-foreground">Submitted Associations</p>
                      {kyc.associations.map((a, i) => <AssociationRow key={i} item={a} idx={i} onChange={() => {}} onRemove={() => {}} disabled />)}
                    </div>
                  )}
                  <Button variant="outline" size="sm" className="rounded-full text-xs border-2" disabled={l2Retracting} onClick={handleL2Retract} data-testid="l2-retract-btn">
                    <Undo2 className="w-3 h-3 mr-1" />{l2Retracting ? "Retracting..." : "Retract & Edit (30min cooldown)"}
                  </Button>
                </div>
              ) : null}

              {l2Status === "rejected" && (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm font-medium text-red-800">Credentials Rejected</p>
                  {kyc?.credentials_review_notes && <p className="text-xs text-red-700 mt-1">{kyc.credentials_review_notes}</p>}
                </div>
              )}
            </div>
          )}

          {kycLevel >= 3 && (
            <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 mb-4" data-testid="l2-verified-badge">
              <CheckCircle className="w-4 h-4 text-green-500" /><span className="text-sm font-medium text-green-800">Level 2 Verified — Professionally Verified</span>
            </div>
          )}

          {/* Unlocked capabilities info */}
          {kycLevel < 3 && (
            <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded">
              <p className="text-xs font-heading uppercase text-muted-foreground mb-2">Complete verification to unlock:</p>
              <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                <span className={kycLevel >= 2 ? "text-green-600" : ""}>Accept Bookings</span>
                <span className={kycLevel >= 3 ? "text-green-600" : ""}>Create Sessions & Events</span>
                <span className={kycLevel >= 4 ? "text-green-600" : ""}>Receive Payouts</span>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
        {[
          { label: "Total Sessions", value: data.total_sessions, icon: CalendarDays, color: "text-court-orange", click: () => navigate("/coach/sessions") },
          { label: "Upcoming", value: (data.upcoming || 0) + (data.upcoming_events || 0), icon: Clock, color: "text-blue-500", click: () => navigate("/coach/sessions") },
          { label: "Completed", value: data.completed, icon: TrendingUp, color: "text-green-500" },
          { label: "Revenue", value: `₱${data.total_earnings?.toLocaleString()}`, icon: Coins, color: "text-lime-volt", click: () => navigate("/revenue-center?view=coach") },
          { label: "Rating", value: data.coach_rating > 0 ? data.coach_rating.toFixed(1) : "New", icon: Star, color: "text-amber-500" },
        ].map((s, i) => (
          <div key={i} onClick={s.click} data-testid={`coach-stat-${i}`}
            className={`bg-white border-2 border-slate-100 p-5 ${s.click ? 'cursor-pointer hover:border-slate-200 hover:-translate-y-0.5' : ''} transition-all`}>
            <s.icon className={`w-5 h-5 ${s.color} mb-2`} />
            <p className="font-heading text-2xl font-bold">{s.value}</p>
            <p className="text-[10px] font-heading uppercase tracking-[0.15em] text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Pending Event Requests Banner */}
      {pendingEvents > 0 && (
        <div className="bg-amber-50 border-2 border-amber-200 p-4 mb-8 flex items-center justify-between cursor-pointer hover:border-amber-300 transition-colors"
          onClick={() => navigate("/coach/event-requests")} data-testid="pending-events-banner">
          <div className="flex items-center gap-3">
            <Inbox className="w-5 h-5 text-amber-600" />
            <div>
              <p className="font-heading text-sm font-bold uppercase">
                {pendingEvents} Event Request{pendingEvents > 1 ? 's' : ''} Pending
              </p>
              <p className="text-xs text-muted-foreground">Venue owners want you for their events</p>
            </div>
          </div>
          <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white rounded-full font-heading uppercase text-xs">
            Review
          </Button>
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex gap-3 mb-8 flex-wrap">
        <Button data-testid="create-session-btn" onClick={() => navigate("/coach/sessions?create=true")}
          className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide px-6 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
          Create Session
        </Button>
        <Button data-testid="view-sessions-btn" onClick={() => navigate("/coach/sessions")} variant="outline" className="rounded-full border-2 font-heading uppercase">
          My Sessions
        </Button>
        <Button data-testid="event-requests-btn" onClick={() => navigate("/coach/event-requests")} variant="outline" className="rounded-full border-2 font-heading uppercase">
          <Inbox className="w-4 h-4 mr-1" />Event Requests
        </Button>
      </div>

      {/* Recent Sessions */}
      <h2 className="font-heading text-xl font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
        <Clock className="w-5 h-5 text-court-orange" />Recent Sessions
      </h2>
      {data.recent_sessions?.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-200">
          <CalendarDays className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="font-heading text-muted-foreground uppercase">No sessions yet</p>
          <p className="text-sm text-muted-foreground mt-1">Create your first training session to get started</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.recent_sessions?.map(s => (
            <div key={s.session_id} onClick={() => navigate(`/coach/sessions/${s.session_id}`)}
              className="bg-white border border-slate-100 p-4 flex items-center gap-4 text-sm cursor-pointer hover:border-slate-200 transition-colors" data-testid={`recent-sess-${s.session_id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-heading font-bold uppercase">{s.title}</p>
                  <Badge className={`${statusStyle[s.status]} text-[10px] font-heading uppercase`}>{s.status}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">{s.session_date} at {s.start_time} - {s.sport} - {s.venue_name || "No venue"}</p>
              </div>
              <div className="text-right">
                <p className="flex items-center gap-1 text-sm"><Users className="w-3 h-3" />{s.participants_count}/{s.slot_limit}</p>
                <p className="text-xs text-muted-foreground">₱{s.price_amount}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
